import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PullRequestHours } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import { pullRequestUrl } from "../lib/github";
import { pullRequestsByRepo, sumCost, sumMoney, type Ledger, type Money } from "../lib/ledger";
import { ExpandRow, MoneyText } from "./LedgerParts";

// ---------------------------------------------------------------------------
// «PRs trabajados» (#1344): primero los repositorios involucrados —horas de sus
// PRs y, para los dueños, lo que costaron según lo que gana cada colaborador— y,
// al abrir un repo, los PRs que se están contando con su tiempo y su costo.
//
// Estas horas miden ACTIVIDAD POR COMMITS (bloques de tiempo real repartidos por
// densidad de commits), no horas registradas: la tarjeta lo dice arriba.
// ---------------------------------------------------------------------------

interface Props {
  pullRequests: PullRequestHours[];
  hoursWithoutPullRequest: number;
  /** Nota «miden actividad por commits, no horas registradas» (ya traducida), si aplica. */
  activityNote: string | null;
  ledger: Ledger | null;
  /** Solo quien ve al equipo ve quién trabajó cada PR. */
  showPeople: boolean;
  tc: ThemeClasses;
}

export default function PullRequestsCard({ pullRequests, hoursWithoutPullRequest, activityNote, ledger, showPeople, tc }: Props) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState<string | null>(null);
  const costs = Boolean(ledger?.costs);
  const priced = useMemo(() => {
    const prs = new Map<string, Money>();
    for (const r of ledger ? pullRequestsByRepo(ledger) : []) for (const p of r.pullRequests) prs.set(p.key, p.money);
    return { prs };
  }, [ledger]);
  const repos = useMemo(() => {
    const map = new Map<string, PullRequestHours[]>();
    for (const p of pullRequests) map.set(p.repo, [...(map.get(p.repo) ?? []), p]);
    return [...map.entries()]
      .map(([repo, list]) => ({ repo, hours: Math.round(list.reduce((n, p) => n + p.hours, 0) * 100) / 100, pullRequests: list }))
      .sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo));
  }, [pullRequests]);
  const none = sumCost([]);

  return (
    <div data-testid="prs-card">
      <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.prs.title")}</p>
      <p className={`mb-2 text-xs ${tc.textSecondary}`}>{t("workHours.prs.subtitle", { hours: hoursWithoutPullRequest })}</p>
      {activityNote && (
        <p className={`mb-2 text-xs ${tc.textSecondary}`} data-testid="prs-activity-note">
          {activityNote}
        </p>
      )}
      {repos.map((r) => (
        <ExpandRow
          key={r.repo}
          tc={tc}
          testId={`prs-repo-${r.repo}`}
          open={open === r.repo}
          onToggle={() => setOpen(open === r.repo ? null : r.repo)}
          label={
            <>
              <span className="break-all">{r.repo}</span>
              <span className={`block text-[11px] ${tc.textSecondary}`}>{t("workHours.prs.count", { count: r.pullRequests.length })}</span>
            </>
          }
          right={
            <>
              <span className="font-semibold">{r.hours} h</span>
              {/* El costo es el de los PRs LISTADOS, no el de todo lo que el libro traiga del repo. */}
              {costs && <MoneyText money={sumMoney(r.pullRequests.map((p) => priced.prs.get(p.key) ?? none))} className={`block text-xs ${tc.textSecondary}`} />}
            </>
          }
        >
          <table className="w-full text-xs" data-testid={`prs-list-${r.repo}`}>
            <thead>
              <tr className={`text-left ${tc.textSecondary}`}>
                <th className="py-1 pr-2">PR</th>
                <th className="py-1 pr-2">{t("workHours.prs.titleCol")}</th>
                <th className="py-1 text-right">{t("workHours.kpi.hours")}</th>
                {costs && <th className="py-1 pl-2 text-right">{t("workHours.ledger.cost")}</th>}
              </tr>
            </thead>
            <tbody>
              {r.pullRequests.map((p) => {
                // La URL viaja en el commit, pero es opcional: si no vino, se
                // deriva de `owner/repo#n` para no pintar un enlace muerto.
                const url = pullRequestUrl(p);
                const label = `#${p.number}`;
                return (
                  <tr key={p.key} className={`border-t align-top ${tc.border} ${tc.text}`}>
                    <td className="whitespace-nowrap py-1 pr-2">
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" title={p.key} className="text-violet-500 hover:underline">
                          {label}
                        </a>
                      ) : (
                        label
                      )}
                    </td>
                    <td className="py-1 pr-2">
                      <span className="line-clamp-2">{p.title ?? "—"}</span>
                      {showPeople && <span className={`block text-[11px] ${tc.textSecondary}`}>{p.users.join(", ")}</span>}
                    </td>
                    <td className="whitespace-nowrap py-1 text-right font-semibold">{p.hours} h</td>
                    {costs && (
                      <td className="whitespace-nowrap py-1 pl-2 text-right">
                        <MoneyText money={priced.prs.get(p.key) ?? none} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ExpandRow>
      ))}
      {repos.length === 0 && <p className={`py-3 text-center text-xs ${tc.textSecondary}`}>{t("workHours.prs.empty")}</p>}
    </div>
  );
}
